/**
 * GridTwinX Global State Store
 *
 * Uses Zustand for lightweight global state management.
 */

import { create } from 'zustand';
import { apiFetch, nzGridApi, NZSolverResult, NZGridSummary } from './services/api';

// Types
export interface Case {
  id: string;
  name: string;
  description?: string;
  bus_count: number;
  branch_count: number;
  created_at: string;
}

export interface Run {
  id: string;
  case_id: string;
  solver_type: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  converged?: boolean;
  iterations?: number;
  created_at: string;
}

export interface SolverConfig {
  solver_type: 'dc_pf' | 'ac_pf' | 'dc_opf';
  tolerance: number;
  max_iterations: number;
  enforce_q_limits: boolean;
  island: string | null;
  load_factor: number;
}

export interface AppState {
  // Connection state
  isConnected: boolean;
  setConnected: (connected: boolean) => void;

  // Current selections
  selectedCaseId: string | null;
  selectCase: (caseId: string | null) => void;

  selectedRunId: string | null;
  selectRun: (runId: string | null) => void;

  // Cases
  cases: Case[];
  setCases: (cases: Case[]) => void;
  addCase: (case_: Case) => void;

  // Runs
  runs: Run[];
  setRuns: (runs: Run[]) => void;
  addRun: (run: Run) => void;
  updateRun: (runId: string, updates: Partial<Run>) => void;

  // Solver config
  solverConfig: SolverConfig;
  setSolverConfig: (config: Partial<SolverConfig>) => void;

  // Solver results
  solverResult: NZSolverResult | null;
  setSolverResult: (result: NZSolverResult | null) => void;

  // Grid summary
  gridSummary: NZGridSummary | null;
  setGridSummary: (summary: NZGridSummary | null) => void;

  // UI State
  activePanelId: string | null;
  setActivePanel: (panelId: string | null) => void;

  // Loading states
  isLoadingCases: boolean;
  isLoadingRuns: boolean;
  isSolving: boolean;
  solverError: string | null;
  setLoading: (key: 'cases' | 'runs' | 'solving', loading: boolean) => void;
  setSolverError: (error: string | null) => void;

  // Actions
  initializeApp: () => Promise<void>;
  runPowerFlow: () => Promise<void>;
  loadGridSummary: () => Promise<void>;
}

export const useStore = create<AppState>((set, get) => ({
  // Connection state
  isConnected: false,
  setConnected: (connected) => set({ isConnected: connected }),

  // Current selections
  selectedCaseId: null,
  selectCase: (caseId) => set({ selectedCaseId: caseId }),

  selectedRunId: null,
  selectRun: (runId) => set({ selectedRunId: runId }),

  // Cases
  cases: [],
  setCases: (cases) => set({ cases }),
  addCase: (case_) =>
    set((state) => ({ cases: [...state.cases, case_] })),

  // Runs
  runs: [],
  setRuns: (runs) => set({ runs }),
  addRun: (run) =>
    set((state) => ({ runs: [run, ...state.runs] })),
  updateRun: (runId, updates) =>
    set((state) => ({
      runs: state.runs.map((run) =>
        run.id === runId ? { ...run, ...updates } : run
      ),
    })),

  // Solver config
  solverConfig: {
    solver_type: 'dc_pf',
    tolerance: 1e-6,
    max_iterations: 20,
    enforce_q_limits: false,
    island: null,
    load_factor: 0.7,
  },
  setSolverConfig: (config) =>
    set((state) => ({
      solverConfig: { ...state.solverConfig, ...config },
    })),

  // Solver results
  solverResult: null,
  setSolverResult: (result) => set({ solverResult: result }),

  // Grid summary
  gridSummary: null,
  setGridSummary: (summary) => set({ gridSummary: summary }),

  // UI State
  activePanelId: 'solver',
  setActivePanel: (panelId) => set({ activePanelId: panelId }),

  // Loading states
  isLoadingCases: false,
  isLoadingRuns: false,
  isSolving: false,
  solverError: null,
  setLoading: (key, loading) => {
    switch (key) {
      case 'cases':
        set({ isLoadingCases: loading });
        break;
      case 'runs':
        set({ isLoadingRuns: loading });
        break;
      case 'solving':
        set({ isSolving: loading });
        break;
    }
  },
  setSolverError: (error) => set({ solverError: error }),

  // Load grid summary
  loadGridSummary: async () => {
    try {
      const summary = await nzGridApi.getSummary();
      set({ gridSummary: summary });
    } catch (e) {
      console.warn('Failed to load grid summary:', e);
    }
  },

  // Run power flow
  runPowerFlow: async () => {
    const { solverConfig, addRun, setSolverResult, setSolverError } = get();

    set({ isSolving: true, solverError: null, solverResult: null });

    try {
      const result = await nzGridApi.solve({
        solver_type: solverConfig.solver_type,
        tolerance: solverConfig.tolerance,
        max_iterations: solverConfig.max_iterations,
        enforce_q_limits: solverConfig.enforce_q_limits,
        island: solverConfig.island || undefined,
        load_factor: solverConfig.load_factor,
      });

      setSolverResult(result);

      // Add to runs history
      addRun({
        id: `run_${Date.now()}`,
        case_id: 'nz_grid',
        solver_type: solverConfig.solver_type,
        status: result.converged ? 'completed' : 'failed',
        converged: result.converged,
        iterations: result.iterations,
        created_at: new Date().toISOString(),
      });

      // Switch to results panel
      set({ activePanelId: 'results' });

    } catch (e: any) {
      console.error('Solver error:', e);
      setSolverError(e.message || 'Solver failed');
    } finally {
      set({ isSolving: false });
    }
  },

  // Initialization
  initializeApp: async () => {
    const { setConnected, loadGridSummary } = get();

    try {
      // Check API health
      const healthResponse = await apiFetch('/health/live');
      if (healthResponse.ok) {
        setConnected(true);
      }

      // Load grid summary
      await loadGridSummary();
      const summary = get().gridSummary;

      // Add NZ Grid as default case
      set({
        cases: [{
          id: 'nz_grid',
          name: 'New Zealand Grid',
          description: summary
            ? `${summary.total_substations} substations, ${summary.total_generators} generators`
            : 'New Zealand Grid',
          bus_count: summary?.total_substations || 0,
          branch_count: summary?.branches?.length || 0,
          created_at: new Date().toISOString(),
        }],
        selectedCaseId: 'nz_grid',
      });

    } catch (e) {
      console.error('Failed to initialize app:', e);
      setConnected(false);
    }
  },
}));

export default useStore;
